<template>
  <div class="menu">
    <!-- With a `label` the trigger IS a UiButton, and that is the whole reason
         this branch exists rather than a slot: a UiButton dropped inside a
         <button> would be a button inside a button, which is invalid HTML and
         which browsers resolve by silently closing the outer one — the menu
         then opens on the first click and never again. One element, one
         button. -->
    <UiButton
      v-if="label"
      ref="trigger"
      :variant="variant"
      :size="size"
      :disabled="disabled"
      :aria-expanded="open"
      aria-haspopup="true"
      @click.stop="toggle"
      @keydown.down.prevent="openWith(0)"
      @keydown.up.prevent="openWith(-1)"
    >{{ label }}</UiButton>

    <button
      v-else
      ref="trigger"
      type="button"
      class="menu__trigger"
      :aria-label="uiLabel('actions')"
      :aria-expanded="open"
      :aria-haspopup="true"
      :disabled="disabled"
      @click.stop="toggle"
      @keydown.down.prevent="openWith(0)"
      @keydown.up.prevent="openWith(-1)"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <circle cx="12" cy="5" r="1.6" fill="currentColor" />
        <circle cx="12" cy="12" r="1.6" fill="currentColor" />
        <circle cx="12" cy="19" r="1.6" fill="currentColor" />
      </svg>
    </button>

    <!-- Teleported, and that is the whole reason this component exists rather
         than a <div> in each caller: a menu positioned inside a table row is
         clipped by the first ancestor with overflow, which every scrolling list
         in this ERP has. Fixed coordinates read off the trigger survive that. -->
    <Teleport to="body">
      <Transition name="slide-up">
        <div
          v-if="open"
          ref="panel"
          class="menu__panel"
          role="menu"
          :style="position"
          @click.stop
        >
          <template v-for="(item, index) in usable" :key="item.id ?? index">
            <hr v-if="item.separator" class="menu__sep" role="separator" />
            <button
              v-else
              type="button"
              role="menuitem"
              class="menu__item"
              :class="{ 'menu__item--danger': item.danger }"
              :disabled="item.disabled"
              :data-index="index"
              @click="pick(item)"
            >
              <span v-if="item.icon" class="menu__icon" aria-hidden="true">{{ item.icon }}</span>
              <span class="menu__label">{{ item.label }}</span>
            </button>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import UiButton from './UiButton.vue'
import { uiLabel } from '../utils/labels'
import { visibleBox } from '../utils/viewport'

/**
 * The row menu: the "⋮" that every list needs and that five screens had begun
 * to grow their own copy of.
 *
 * Items are DATA, not slots, so the caller lists what can be done to a row and
 * this decides how it looks and behaves — which is what keeps the menu in
 * Documentos and the one in Almacén the same menu. An item is:
 *
 *   { id, label, icon?, danger?, disabled?, separator? }
 *
 * and picking one emits `select` with it. Nothing is done here: the caller
 * owns the action, this owns the affordance.
 *
 * Wording comes from the caller already translated (§16): the ui-kit holds no
 * translations, only the shared labels the shell installs through
 * configureLabels().
 */
const props = defineProps({
  /** @type {Array<{id?: string, label?: string, icon?: string, danger?: boolean, disabled?: boolean, separator?: boolean}>} */
  items: { type: Array, default: () => [] },
  /**
   * Given, the trigger is an ordinary button with this text — the «+ Nuevo»
   * shape. Left out, it is the bare ⋮ a table row wants.
   */
  label: { type: String, default: '' },
  /** Only with `label`: passed straight through to UiButton. */
  variant: { type: String, default: 'primary' },
  size: { type: String, default: 'md' },
  /** Which side of the trigger the panel lines up with. */
  align: { type: String, default: 'end' },   // start | end
  disabled: Boolean,
})

const emit = defineEmits(['select', 'open', 'close'])

const open = ref(false)
const trigger = ref(null)
/** A ref on a component is its instance; on a plain button it is the node. */
const triggerEl = () => trigger.value?.$el ?? trigger.value
const panel = ref(null)
const position = ref({})

// A separator that lands first or last draws a line against nothing, and two in
// a row draw two. Callers build their list with v-if-style holes in it, so the
// tidying belongs here rather than in every caller.
const usable = computed(() => {
  const items = props.items.filter(Boolean)
  const out = []
  for (const item of items) {
    if (!item.separator) { out.push(item); continue }
    if (out.length && !out[out.length - 1].separator) out.push(item)
  }
  while (out.length && out[out.length - 1].separator) out.pop()
  return out
})

/**
 * Where the panel goes, in viewport coordinates.
 *
 * Flipped above the trigger when there is no room below, and pulled back inside
 * the right edge — a menu on the last row of a full-height table opens off the
 * bottom of the window otherwise, and its last item is the destructive one.
 */
function place() {
  const box = triggerEl()?.getBoundingClientRect()
  if (!box) return

  const width = 220
  const estimated = usable.value.length * 36 + 16
  // Lo que se ve y no la ventana: con el teclado fuera (iOS) el sitio de
  // debajo es el de las teclas.
  const visible = visibleBox()
  const below = visible.bottom - box.bottom

  const top = below < estimated && box.top - visible.top > estimated
    ? { bottom: `${visible.layoutHeight - box.top + 6}px` }
    : { top: `${box.bottom + 6}px` }

  const left = props.align === 'start'
    ? Math.min(box.left, window.innerWidth - width - 8)
    : Math.min(Math.max(box.right - width, 8), window.innerWidth - width - 8)

  position.value = { ...top, left: `${Math.max(8, left)}px`, width: `${width}px` }
}

async function show(focusIndex = null) {
  if (props.disabled) return
  open.value = true
  emit('open')
  await nextTick()
  place()
  if (focusIndex !== null) focusItem(focusIndex)
}

function hide() {
  if (!open.value) return
  open.value = false
  emit('close')
}

const toggle = () => (open.value ? hide() : show())
const openWith = (index) => show(index)

function focusItem(index) {
  const buttons = [...(panel.value?.querySelectorAll('.menu__item:not([disabled])') ?? [])]
  if (!buttons.length) return
  const target = index < 0 ? buttons[buttons.length - 1] : buttons[Math.min(index, buttons.length - 1)]
  target?.focus()
}

function pick(item) {
  if (item.disabled) return
  hide()
  emit('select', item)
  // Back to the trigger: a menu opened from the keyboard that leaves focus on
  // <body> drops the person to the top of the page on their next Tab.
  triggerEl()?.focus()
}

function onDocumentDown() { hide() }

function onKey(event) {
  if (!open.value) return

  if (event.key === 'Escape') {
    event.stopPropagation()
    hide()
    triggerEl()?.focus()
    return
  }

  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  event.preventDefault()

  const buttons = [...(panel.value?.querySelectorAll('.menu__item:not([disabled])') ?? [])]
  const at = buttons.indexOf(document.activeElement)
  const step = event.key === 'ArrowDown' ? 1 : -1
  const next = (at + step + buttons.length) % (buttons.length || 1)
  buttons[next]?.focus()
}

watch(open, (isOpen) => {
  const method = isOpen ? 'addEventListener' : 'removeEventListener'
  document[method]('mousedown', onDocumentDown)
  document[method]('keydown', onKey, true)
  // Capture, so a menu inside a scrolling pane closes instead of floating away
  // from the row it belongs to.
  window[method]('scroll', hide, true)
  window[method]('resize', hide)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentDown)
  document.removeEventListener('keydown', onKey, true)
  window.removeEventListener('scroll', hide, true)
  window.removeEventListener('resize', hide)
})

defineExpose({ hide })
</script>

<style scoped>
.menu { display: inline-flex; }

.menu__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: none;
  background: none;
  border-radius: var(--radius-sm);
  color: var(--color-text-soft);
  cursor: pointer;
  transition: background var(--fast), color var(--fast);
}
.menu__trigger:hover:not(:disabled) { background: var(--color-surface-2); color: var(--color-text); }
.menu__trigger[aria-expanded='true'] { background: var(--color-surface-2); color: var(--color-text); }
.menu__trigger:disabled { opacity: 0.4; cursor: not-allowed; }

/* The same finger UiButton grows its small button for: 30px is a miss on a
   phone, and this trigger is what sits next to that button in a row's actions
   — at 30 against 36 it also stood a head shorter than its neighbour. */
@media (pointer: coarse) {
  .menu__trigger { width: 2.25rem; height: 2.25rem; }
}

/* ── Con el dedo ────────────────────────────────────────────────────────────
   2,75rem y no 2,25: 36 px se queda corto, y detrás de este botón
   están las acciones destructivas de la pantalla.
   `pointer: coarse` mira con qué se toca, no cuánto mide la ventana. */
@media (pointer: coarse) {
  .menu__trigger { width: 2.75rem; height: 2.75rem; }
}
.menu__panel {
  position: fixed;
  z-index: 70;
  padding: var(--space-2);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.menu__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  transition: background var(--fast), color var(--fast);
}
.menu__item:hover:not(:disabled),
.menu__item:focus-visible { background: var(--color-surface-2); outline: none; }
.menu__item:disabled { opacity: 0.45; cursor: not-allowed; }
.menu__item--danger { color: var(--color-danger); }
.menu__item--danger:hover:not(:disabled),
.menu__item--danger:focus-visible { background: var(--color-danger-soft); }

.menu__icon { width: 1.1rem; text-align: center; flex-shrink: 0; }
.menu__label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.menu__sep {
  height: 1px;
  border: none;
  background: var(--color-border);
  margin: var(--space-1) 0;
}
</style>
